<template>
  <transition name="fade" mode="out-in">
    <AgGridVue
      :key="isDark ? 'dark' : 'light'"
      style="flex: 1;"
      :grid-options="options"
      :rowData="rowData"
      :overlayNoRowsTemplate="noRowsTemplate"
      @grid-ready="onGridReady"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
    />
  </transition>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { AgGridVue } from 'ag-grid-vue3'
import { GridApi, GridOptions } from 'ag-grid-community';
import { themeQuartz, colorSchemeDark } from 'ag-grid-community';
import { MemberDetails, MemberRow, ScopeRefKind } from '@/types/member.js';
import { useAllOrgnisationRoles } from '@/queries/organisation.query';
import { fetchMemberGrants, useDeleteMemberGrant, useMemberGrants, useMembersDetails } from '@/queries/members.query.js';
import RoleActions from './cells/RoleActions.vue';
import { DefaultAppRole, MemberRole } from '@/types/role.js';
import { useToast } from 'vue-toastification';
import { useQueries } from '@tanstack/vue-query';
import { membersApi } from '@/api/members.api.js';
import { queryClient } from '@/queries/queryClient.js';

const toast = useToast();
const isDark = useDark();
const { t } = useI18n();

let gridApi: GridApi;

const noRowsTemplate = ref(`<div>${t('app.tables.administratorTable.noRowTemplate')}</div>`);

const appStore = useAppStore();
const { data: members, isPending: isMembersPending } = useMembersDetails();
const { mutateAsync: deleteMemberGrant } = useDeleteMemberGrant();

const queries = computed(() =>  {
  if (isMembersPending.value) return [];

  return members.value.map((member) => {
    return {
      queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: member.member.id }, 'grants'],
      queryFn: async () => {
        const grants = await membersApi.getGrants(member.member.id);
        for (const g of grants) {
          queryClient.setQueryData(['members', { organisationId: appStore.currentOrganisationId, memberId: member.member.id }, 'grants', { appGrantId: g.app_grant_id }], g);
        }
        return grants;
      }
    }
  });
});

const allMemberGrantsQueries = useQueries({ queries: queries });

const emit = defineEmits<{
  (emit: "remove-administrator", value: MemberDetails): void;
}>();

const props = defineProps<{
  workspaceId: string,
}>();

const options = computed(() => {
  return {
    theme: isDark.value ? themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#1F1F1F",
      fontFamily: 'Inter, sans-serif',
    }).withPart(colorSchemeDark) : themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#FAFAFA",
      fontFamily: 'Inter, sans-serif',
    }),
    suppressCellFocus: true,
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
    },
    defaultColDef: {
      sortable: true,
      flex: 1,
      resizable: false,
      unSortIcon: true
    },
    columnDefs: [
      { field: 'email', headerName: t('other.table.columns.email'), flex: 1 },
      { field: 'name', headerName: t('other.table.columns.user'), flex: 1 },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filter: false,
        flex: 0,
        cellRenderer: RoleActions,
        cellRendererParams: {
          onDelete: (row: MemberRow) => removeAdministrator(row),
        }
      },
    ]
  } as GridOptions<MemberRow>;
});

const rowData = computed(() => {
  if (isMembersPending.value) return [];
  return members?.value?.filter((m, index) => {
    const memberGrants = allMemberGrantsQueries.value?.[index];
    return memberGrants.data?.some((grant) => (grant.role.name === DefaultAppRole.ADMINISTRATOR && grant.scope.kind === ScopeRefKind.WORKSPACE && grant.scope.id === props?.workspaceId));
  })
  .map((v) => {
    return {
      id: v.member.id,
      name: `${v.account.personal_information.first_name} ${v.account.personal_information.last_name}`,
      email: v.account.personal_information.email,
      role: DefaultAppRole.ADMINISTRATOR,
    } as MemberRow;
  });
});

async function removeAdministrator(memberRow: MemberRow) {
  if (memberRow?.id == undefined || memberRow?.id === null) return;
  try {
    const memberGrants = await fetchMemberGrants(memberRow.id);
    const administratorGrant = memberGrants.find((grant) => (grant.role.name === DefaultAppRole.ADMINISTRATOR && grant.scope.kind === ScopeRefKind.WORKSPACE && grant.scope.id === props?.workspaceId));
    await deleteMemberGrant({
      memberId: memberRow.id,
      memberGrantId: administratorGrant.member_grant_id,
    }, {
      onSuccess: () => {
        toast.success(t('app.toasts.success'));
      },
      onError: () => {
        toast.error(t('other.error.ERROR_UNKNOWN'));
      }
    });
  } catch (e) {
    console.error("Error: ", e);
    toast.error(t('other.error.ERROR_UNKNOWN'));
  }
}

function onGridReady(params) {
  gridApi = params.api
}

function onSelectionChanged() {
  // const selectedRows = gridApi.getSelectedRows();
  // console.log('Selected:', selectedRows);
}

function onFirstDataRendered() {
  gridApi.autoSizeColumns(['actions']);
}
</script>

<style lang="scss"></style>